import type { RootState } from './store';

export type GuestTokenLike =
  | string
  | { apiToken?: string; apiKey?: string }
  | null
  | undefined;

export type ClaimIntakeApiConfig = {
  /** Override bearer resolution (optional). */
  getBearerToken?: (state: RootState) => string | null | undefined;
  /** Silent guest JWT refresh — same pattern as chat `fetchLatestGuestToken`. */
  fetchLatestGuestToken?: () => Promise<string | null | undefined>;
};

let claimIntakeApiConfig: ClaimIntakeApiConfig = {};

export function setClaimIntakeApiConfig(config: ClaimIntakeApiConfig): void {
  claimIntakeApiConfig = { ...claimIntakeApiConfig, ...config };
}

export function getClaimIntakeApiConfig(): Readonly<ClaimIntakeApiConfig> {
  return claimIntakeApiConfig;
}

/** True when claim intake should call guest `/rest/v1` (not logged-in auth token). */
export function shouldUseClaimIntakeRestPaths(state: RootState): boolean {
  const auth = state.auth?.token;
  const hasAuthToken = typeof auth === 'string' && auth.trim() !== '';
  if (Boolean(state.auth?.user?.isAuthenticated) && hasAuthToken) {
    return false;
  }
  // Token synced from Auth0 but `isAuthenticated` not set yet (e.g. PAL `/claim-intake` outside Layout).
  if (hasAuthToken) {
    return false;
  }
  return true;
}

/** Maps `/api/v1/...` → `/rest/v1/...` for guest claim-intake REST calls. */
export function toClaimIntakeApiPath(apiPath: string, useRest: boolean): string {
  if (!useRest) return apiPath;
  return apiPath.replace(/^\/api\/v1/, '/rest/v1');
}

export function readGuestApiToken(guest: GuestTokenLike): string | undefined {
  if (!guest) return undefined;
  if (typeof guest === 'string') {
    const t = guest.trim();
    return t || undefined;
  }
  const t = guest.apiToken?.trim();
  return t || undefined;
}

/**
 * Bearer for claim intake — mirrors chat `converseToken`:
 * logged-in → `state.auth.token`; guest → `state.advisor.guestToken.apiToken`.
 */
export function resolveClaimIntakeBearerToken(state: RootState): string | null | undefined {
  if (claimIntakeApiConfig.getBearerToken) {
    return claimIntakeApiConfig.getBearerToken(state);
  }

  const auth = state.auth?.token;
  const authTrimmed = typeof auth === 'string' && auth.trim() ? auth.trim() : '';
  const guest = readGuestApiToken(state.advisor?.guestToken as GuestTokenLike);

  // Prefer auth over stale guest token (e.g. right after login on `/claim-intake`).
  if (authTrimmed) {
    return authTrimmed;
  }
  if (guest) {
    return guest;
  }
  return null;
}

/** Guest-only Bearer header for `/rest/v1/...` (logged-in uses default RTK `prepareHeaders`). */
export function buildClaimIntakeRequestHeaders(
  getState: () => unknown
): Record<string, string> {
  const state = getState() as RootState;
  if (!shouldUseClaimIntakeRestPaths(state)) return {};
  const bearer = resolveClaimIntakeBearerToken(state);
  return bearer ? { Authorization: `Bearer ${bearer}` } : {};
}
